import { useState, useEffect, useRef } from "react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API_BASE = "/api";

// ════════════════════════════════════════════════════════════════════
// LOGIN SCREEN
// ════════════════════════════════════════════════════════════════════
function LoginScreen({ onLogin }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res  = await fetch(`${API_BASE}/auth/login`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Login failed");
      onLogin(data.access_token, data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const iS = { width:"100%", background:"#0f172a", border:"1.5px solid #1e293b", borderRadius:9, padding:"11px 14px", fontSize:14, color:"#f1f5f9", outline:"none", boxSizing:"border-box" };
  const lS = { display:"block", fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 };

  return (
    <div style={{ height:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#080e1a", fontFamily:"'IBM Plex Sans','Segoe UI',sans-serif" }}>
      <div style={{ background:"#0a1628", border:"1px solid #1e293b", borderRadius:18, padding:"40px 44px", width:"100%", maxWidth:420, boxShadow:"0 25px 60px rgba(0,0,0,0.4)" }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ width:50, height:50, borderRadius:14, background:"linear-gradient(135deg,#3b82f6,#06b6d4)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, fontWeight:900, color:"#fff", margin:"0 auto 14px" }}>N</div>
          <h1 style={{ fontSize:22, fontWeight:800, color:"#f1f5f9", margin:"0 0 4px" }}>NEMT Hub</h1>
          <p style={{ fontSize:13, color:"#475569", margin:0 }}>Sign in to your account</p>
        </div>
        <form onSubmit={handle}>
          <div style={{ marginBottom:16 }}>
            <label style={lS}>Email</label>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="admin@nemthub.com" required style={iS} />
          </div>
          <div style={{ marginBottom:24 }}>
            <label style={lS}>Password</label>
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" required style={iS} />
          </div>
          {error && <p style={{ color:"#f87171", fontSize:13, marginBottom:16, textAlign:"center" }}>{error}</p>}
          <button type="submit" disabled={loading} style={{ width:"100%", padding:"12px", background:loading?"#1e3a5f":"#2563eb", border:"none", borderRadius:9, fontSize:14, fontWeight:700, color:"#fff", cursor:loading?"not-allowed":"pointer" }}>
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// SHARED CONSTANTS & UTILITIES
// ════════════════════════════════════════════════════════════════════
const VEHICLE_TYPES_LIST = [
  { id:"sedan",    label:"Standard Sedan",    icon:"🚗", desc:"Ambulatory, no equipment needed" },
  { id:"wav",      label:"Wheelchair Van",     icon:"♿", desc:"Wheelchair accessible vehicle" },
  { id:"stretcher",label:"Stretcher Van",      icon:"🚐", desc:"Non-emergency stretcher transport" },
  { id:"gurney",   label:"Gurney Transport",   icon:"🏥", desc:"Requires gurney & attendant" },
  { id:"bariatric",label:"Bariatric Vehicle",  icon:"🚌", desc:"Oversized/bariatric equipment" },
];
const VEHICLE_ICONS  = { sedan:"🚗", wav:"♿", stretcher:"🚐", gurney:"🏥", bariatric:"🚌" };
const VEHICLE_LABELS = { sedan:"Sedan", wav:"Wheelchair Van", stretcher:"Stretcher Van", gurney:"Gurney", bariatric:"Bariatric" };
const PAYER_TYPES    = ["Health Plan","Medicaid","Medicare","Private Pay"];
const PAYER_MAP      = {"Health Plan":"health_plan","Medicaid":"medicaid","Medicare":"medicare","Private Pay":"private"};

const STATUS_META_DARK = {
  new:       { label:"New",       bg:"#451a03", text:"#fb923c", dot:"#f59e0b" },
  assigned:  { label:"Assigned",  bg:"#1e3a5f", text:"#60a5fa", dot:"#3b82f6" },
  en_route:  { label:"En Route",  bg:"#2e1065", text:"#c4b5fd", dot:"#8b5cf6" },
  completed: { label:"Completed", bg:"#052e16", text:"#4ade80", dot:"#22c55e" },
  cancelled: { label:"Cancelled", bg:"#1e293b", text:"#94a3b8", dot:"#475569" },
};

function DarkStatusPill({ status }) {
  const m = STATUS_META_DARK[status] || STATUS_META_DARK.new;
  return (
    <span style={{ background:m.bg, color:m.text, borderRadius:99, padding:"2px 9px", fontSize:11, fontWeight:700, display:"inline-flex", alignItems:"center", gap:5 }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:m.dot, display:"inline-block" }} />
      {m.label}
    </span>
  );
}

function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

// ════════════════════════════════════════════════════════════════════
// MODULE 1 — SCHEDULER
// ════════════════════════════════════════════════════════════════════
const SCHED_PROVIDERS = [
  { id:"p1", name:"CityRide Medical Transport",  vehicles:["sedan","wav","stretcher"],              eta:"12 min", rating:4.8, available:3 },
  { id:"p2", name:"Metro Access Transport",       vehicles:["sedan","wav","stretcher","gurney","bariatric"], eta:"18 min", rating:4.6, available:2 },
  { id:"p3", name:"QuickCare Rides",              vehicles:["sedan","wav"],                          eta:"9 min",  rating:4.9, available:5 },
  { id:"p4", name:"AllCare Medical Transit",      vehicles:["sedan","wav","stretcher","gurney"],     eta:"22 min", rating:4.5, available:1 },
];
const SCHED_STEPS = ["Member","Trip Details","Vehicle & Provider","Confirm"];

// Shared inline style tokens for the scheduler (light theme)
const IS = {
  card:     { background:"#fff", borderRadius:14, border:"1px solid #e2e8f0", padding:"22px 24px", boxShadow:"0 1px 4px rgba(0,0,0,0.05)", marginBottom:18 },
  label:    { display:"block", fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 },
  input:    { width:"100%", background:"#fff", border:"1.5px solid #cbd5e1", borderRadius:9, padding:"11px 14px", fontSize:14, color:"#1e293b", outline:"none", boxSizing:"border-box", height:46 },
  select:   { width:"100%", background:"#fff", border:"1.5px solid #cbd5e1", borderRadius:9, padding:"11px 14px", fontSize:14, color:"#1e293b", outline:"none", boxSizing:"border-box", height:46, cursor:"pointer" },
  textarea: { width:"100%", background:"#fff", border:"1.5px solid #cbd5e1", borderRadius:9, padding:"11px 14px", fontSize:14, color:"#1e293b", outline:"none", boxSizing:"border-box", resize:"vertical" },
  grid2:    { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 },
  secTitle: { fontSize:10, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.09em", margin:"0 0 16px" },
  btnPrim:  { padding:"11px 26px", background:"#2563eb", border:"none", borderRadius:9, fontSize:14, fontWeight:700, color:"#fff", cursor:"pointer" },
  btnSec:   { padding:"11px 20px", background:"transparent", border:"none", borderRadius:9, fontSize:14, fontWeight:600, color:"#64748b", cursor:"pointer" },
  btnDis:   { padding:"11px 26px", background:"#e2e8f0", border:"none", borderRadius:9, fontSize:14, fontWeight:700, color:"#94a3b8", cursor:"not-allowed" },
};

function SBadge({ color, children }) {
  const cols = {
    blue:  { background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe" },
    green: { background:"#f0fdf4", color:"#15803d", border:"1px solid #bbf7d0" },
    amber: { background:"#fffbeb", color:"#b45309", border:"1px solid #fde68a" },
    slate: { background:"#f8fafc", color:"#475569", border:"1px solid #e2e8f0" },
  };
  return (
    <span style={{ ...(cols[color]||cols.slate), display:"inline-flex", alignItems:"center", gap:4, padding:"2px 8px", borderRadius:99, fontSize:11, fontWeight:600 }}>
      {children}
    </span>
  );
}

function SchedStepIndicator({ current }) {
  return (
    <div style={{ display:"flex", alignItems:"center", background:"#fff", border:"1px solid #e2e8f0", borderRadius:14, padding:"16px 22px", marginBottom:24, boxShadow:"0 1px 4px rgba(0,0,0,0.05)" }}>
      {SCHED_STEPS.map((step, i) => (
        <div key={step} style={{ display:"flex", alignItems:"center", flex: i < SCHED_STEPS.length - 1 ? 1 : "none", minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
            <div style={{
              width:34, height:34, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:13, fontWeight:800, flexShrink:0,
              background: i < current ? "#2563eb" : i === current ? "#fff" : "#f8fafc",
              border: i <= current ? "2px solid #2563eb" : "2px solid #e2e8f0",
              color: i < current ? "#fff" : i === current ? "#2563eb" : "#94a3b8",
            }}>
              {i < current ? "✓" : i + 1}
            </div>
            <div style={{ minWidth:0 }}>
              <p style={{ margin:0, fontSize:13, fontWeight:700, color: i === current ? "#2563eb" : i < current ? "#475569" : "#94a3b8", whiteSpace:"nowrap" }}>{step}</p>
              <p style={{ margin:0, fontSize:10, color: i < current ? "#22c55e" : i === current ? "#94a3b8" : "#cbd5e1", whiteSpace:"nowrap" }}>
                {i < current ? "✓ Complete" : i === current ? "In progress" : "Pending"}
              </p>
            </div>
          </div>
          {i < SCHED_STEPS.length - 1 && (
            <div style={{ flex:1, height:2, background: i < current ? "#2563eb" : "#e2e8f0", margin:"0 14px", borderRadius:2, minWidth:16 }} />
          )}
        </div>
      ))}
    </div>
  );
}

function fmtPhone(val) {
  const digits = val.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0,3)}-${digits.slice(3)}`;
  return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
}

function SchedMember({ d, ch, onNext }) {
  const ok = d.firstName && d.lastName && d.dob && d.memberId && d.phone;
  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:22, fontWeight:800, color:"#0f172a", margin:"0 0 4px", letterSpacing:"-0.02em" }}>Member Information</h2>
        <p style={{ fontSize:13, color:"#64748b", margin:0 }}>Look up or enter the member requesting transport.</p>
      </div>

      {/* Member lookup */}
      <div style={IS.card}>
        <p style={IS.secTitle}>Search Existing Member</p>
        <div style={{ display:"flex", gap:10 }}>
          <input style={{ ...IS.input, flex:1 }} placeholder="Search by Member ID or name..." tabIndex={1} />
          <button style={{ padding:"0 18px", background:"#f1f5f9", border:"1.5px solid #e2e8f0", borderRadius:9, fontSize:13, fontWeight:600, color:"#475569", cursor:"pointer", whiteSpace:"nowrap", height:46 }}>Look Up</button>
        </div>
      </div>

      {/* Member details */}
      <div style={IS.card}>
        <p style={IS.secTitle}>Member Details</p>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div style={IS.grid2}>
            <div>
              <label style={IS.label}>First Name *</label>
              <input style={IS.input} value={d.firstName} onChange={e=>ch("firstName",e.target.value)} placeholder="First name" tabIndex={2} />
            </div>
            <div>
              <label style={IS.label}>Last Name *</label>
              <input style={IS.input} value={d.lastName} onChange={e=>ch("lastName",e.target.value)} placeholder="Last name" tabIndex={3} />
            </div>
          </div>
          <div style={IS.grid2}>
            <div>
              <label style={IS.label}>Date of Birth *</label>
              <input type="date" style={IS.input} value={d.dob} onChange={e=>ch("dob",e.target.value)} tabIndex={4} />
            </div>
            <div>
              <label style={IS.label}>Member ID *</label>
              <input style={IS.input} value={d.memberId} onChange={e=>ch("memberId",e.target.value)} placeholder="e.g. HM-8820041" tabIndex={5} />
            </div>
          </div>
          <div style={IS.grid2}>
            <div>
              <label style={IS.label}>Phone *</label>
              <input style={IS.input} value={d.phone} onChange={e=>ch("phone",fmtPhone(e.target.value))} placeholder="555-000-0000" inputMode="numeric" tabIndex={6} />
            </div>
            <div>
              <label style={IS.label}>Payer / Plan</label>
              <select style={IS.select} value={d.payer} onChange={e=>ch("payer",e.target.value)} tabIndex={7}>
                <option value="">Select payer...</option>
                {PAYER_TYPES.map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={IS.label}>Special Needs / Notes</label>
            <textarea style={{ ...IS.textarea, height:80 }} rows={3} value={d.notes} onChange={e=>ch("notes",e.target.value)} placeholder="Oxygen required, non-English speaker, etc." tabIndex={8} />
          </div>
        </div>
      </div>

      <div style={{ display:"flex", justifyContent:"flex-end" }}>
        <button disabled={!ok} onClick={onNext} tabIndex={9} style={ok ? IS.btnPrim : IS.btnDis}>
          Continue →
        </button>
      </div>
    </div>
  );
}

function AddressInput({ label, icon, value, onChange, placeholder, tabIndex }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const timer = useRef(null);

  const handleChange = (val) => {
    setQuery(val);
    onChange(val);
    clearTimeout(timer.current);
    if (val.length < 4) { setSuggestions([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&countrycodes=us&q=${encodeURIComponent(val)}`,
          { headers: { "Accept-Language": "en" } }
        );
        const data = await res.json();
        setSuggestions(data.map(r => r.display_name));
        setOpen(data.length > 0);
      } catch { setSuggestions([]); setOpen(false); }
    }, 350);
  };

  const pick = (addr) => {
    setQuery(addr);
    onChange(addr);
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div style={{ position:"relative" }}>
      <label style={IS.label}>{label}</label>
      <div style={{ position:"relative" }}>
        <span style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", fontSize:15, pointerEvents:"none", lineHeight:1 }}>{icon}</span>
        <input
          style={{ ...IS.input, paddingLeft:38 }}
          value={query}
          onChange={e => handleChange(e.target.value)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          tabIndex={tabIndex}
          autoComplete="off"
        />
      </div>
      {open && (
        <ul style={{ position:"absolute", zIndex:50, left:0, right:0, top:"100%", marginTop:4, background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:10, boxShadow:"0 8px 24px rgba(0,0,0,0.12)", overflow:"hidden", maxHeight:210, overflowY:"auto", listStyle:"none", padding:0, margin:"4px 0 0" }}>
          {suggestions.map((s, i) => (
            <li key={i} onMouseDown={() => pick(s)}
              style={{ padding:"10px 14px", fontSize:13, color:"#1e293b", cursor:"pointer", borderBottom:"1px solid #f1f5f9", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              📍 {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SchedTrip({ d, ch, onNext, onBack }) {
  const ok = d.pickup && d.dropoff && d.date && d.time && d.purpose;
  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:22, fontWeight:800, color:"#0f172a", margin:"0 0 4px", letterSpacing:"-0.02em" }}>Trip Details</h2>
        <p style={{ fontSize:13, color:"#64748b", margin:0 }}>Enter pickup, destination, and appointment info.</p>
      </div>

      <div style={IS.card}>
        <p style={IS.secTitle}>Addresses</p>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <AddressInput label="Pickup Address *" icon="📍" value={d.pickup} onChange={v=>ch("pickup",v)} placeholder="Street, City, State, ZIP" tabIndex={1} />
          <AddressInput label="Destination Address *" icon="🏁" value={d.dropoff} onChange={v=>ch("dropoff",v)} placeholder="Street, City, State, ZIP" tabIndex={2} />
        </div>
      </div>

      <div style={IS.card}>
        <p style={IS.secTitle}>Appointment Details</p>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div style={IS.grid2}>
            <div>
              <label style={IS.label}>Pickup Date *</label>
              <input type="date" style={IS.input} value={d.date} onChange={e=>ch("date",e.target.value)} tabIndex={3} />
            </div>
            <div>
              <label style={IS.label}>Pickup Time *</label>
              <input type="time" step="900" style={IS.input} value={d.time} onChange={e=>ch("time",e.target.value)} tabIndex={4} />
            </div>
          </div>
          <div>
            <label style={IS.label}>Trip Purpose *</label>
            <select style={IS.select} value={d.purpose} onChange={e=>ch("purpose",e.target.value)} tabIndex={5}>
              <option value="">Select purpose...</option>
              {["Primary Care Visit","Specialist Appointment","Dialysis","Physical Therapy","Mental Health","Lab / Imaging","Hospital Discharge","Pharmacy","Other"].map(p=><option key={p}>{p}</option>)}
            </select>
          </div>
          <div style={IS.grid2}>
            <div>
              <label style={IS.label}>Return Trip?</label>
              <select style={IS.select} value={d.returnTrip} onChange={e=>ch("returnTrip",e.target.value)} tabIndex={6}>
                <option value="no">No</option>
                <option value="yes">Yes — schedule return</option>
                <option value="wait">Yes — driver waits</option>
                <option value="willcall">Yes — will call</option>
              </select>
            </div>
            <div>
              <label style={IS.label}>Ordering Provider</label>
              <input style={IS.input} value={d.provider} onChange={e=>ch("provider",e.target.value)} placeholder="Provider name or NPI" tabIndex={7} />
            </div>
          </div>
        </div>
      </div>

      <div style={{ display:"flex", justifyContent:"space-between" }}>
        <button onClick={onBack} tabIndex={8} style={IS.btnSec}>← Back</button>
        <button disabled={!ok} onClick={onNext} tabIndex={9} style={ok ? IS.btnPrim : IS.btnDis}>Continue →</button>
      </div>
    </div>
  );
}

function SchedDispatch({ d, ch, onNext, onBack }) {
  const ok = d.vehicleType && d.providerId;
  const eligible = SCHED_PROVIDERS.filter(p => d.vehicleType ? p.vehicles.includes(d.vehicleType) : true);

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:22, fontWeight:800, color:"#0f172a", margin:"0 0 4px", letterSpacing:"-0.02em" }}>Vehicle & Provider</h2>
        <p style={{ fontSize:13, color:"#64748b", margin:0 }}>Complete both steps below to continue.</p>
      </div>

      {/* Step 1: Vehicle type */}
      <div style={IS.card}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
          <div style={{ width:28, height:28, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:800, flexShrink:0, background: d.vehicleType ? "#2563eb" : "#f1f5f9", color: d.vehicleType ? "#fff" : "#64748b", border: d.vehicleType ? "2px solid #2563eb" : "2px solid #cbd5e1" }}>
            {d.vehicleType ? "✓" : "1"}
          </div>
          <p style={{ margin:0, fontSize:14, fontWeight:700, color:"#1e293b" }}>Select Vehicle Type</p>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {VEHICLE_TYPES_LIST.map(v => {
            const sel = d.vehicleType === v.id;
            return (
              <label key={v.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", borderRadius:10, border: sel ? "2px solid #2563eb" : "1.5px solid #e2e8f0", background: sel ? "#eff6ff" : "#fff", cursor:"pointer" }}>
                <input type="radio" style={{ display:"none" }} checked={sel} onChange={() => { ch("vehicleType", v.id); ch("providerId", ""); }} />
                <div style={{ width:42, height:42, borderRadius:10, background: sel ? "#dbeafe" : "#f8fafc", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{v.icon}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ margin:0, fontSize:14, fontWeight:700, color: sel ? "#1d4ed8" : "#1e293b" }}>{v.label}</p>
                  <p style={{ margin:0, fontSize:12, color:"#64748b", marginTop:2 }}>{v.desc}</p>
                </div>
                <div style={{ width:22, height:22, borderRadius:"50%", border: sel ? "2px solid #2563eb" : "2px solid #cbd5e1", background: sel ? "#2563eb" : "transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:11, fontWeight:900, color:"#fff" }}>
                  {sel && "✓"}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Step 2: Provider */}
      <div style={{ ...IS.card, opacity: d.vehicleType ? 1 : 0.5, pointerEvents: d.vehicleType ? "auto" : "none" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
          <div style={{ width:28, height:28, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:800, flexShrink:0, background: d.providerId ? "#2563eb" : "#f1f5f9", color: d.providerId ? "#fff" : "#64748b", border: d.providerId ? "2px solid #2563eb" : "2px solid #cbd5e1" }}>
            {d.providerId ? "✓" : "2"}
          </div>
          <p style={{ margin:0, fontSize:14, fontWeight:700, color:"#1e293b" }}>
            Select Provider {d.vehicleType && <span style={{ fontSize:12, fontWeight:500, color:"#94a3b8" }}>— {eligible.length} available</span>}
          </p>
          {!d.vehicleType && <span style={{ fontSize:11, color:"#94a3b8", fontStyle:"italic" }}>(select vehicle type first)</span>}
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {(d.vehicleType ? eligible : SCHED_PROVIDERS).map(p => {
            const sel = d.providerId === p.id;
            const initials = p.name.split(" ").map(w => w[0]).join("").slice(0,2);
            return (
              <label key={p.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", borderRadius:10, border: sel ? "2px solid #2563eb" : "1.5px solid #e2e8f0", background: sel ? "#eff6ff" : "#fff", cursor:"pointer" }}>
                <input type="radio" style={{ display:"none" }} checked={sel} onChange={() => ch("providerId", p.id)} />
                <div style={{ width:42, height:42, borderRadius:10, background: sel ? "#dbeafe" : "#f1f5f9", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800, color: sel ? "#1d4ed8" : "#475569", flexShrink:0 }}>{initials}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ margin:0, fontSize:14, fontWeight:700, color: sel ? "#1d4ed8" : "#1e293b" }}>{p.name}</p>
                  <div style={{ display:"flex", gap:6, marginTop:5, flexWrap:"wrap" }}>
                    <SBadge color="slate">⭐ {p.rating}</SBadge>
                    <SBadge color="green">🕐 {p.eta} ETA</SBadge>
                    <SBadge color="blue">{p.available} available</SBadge>
                  </div>
                </div>
                <div style={{ width:22, height:22, borderRadius:"50%", border: sel ? "2px solid #2563eb" : "2px solid #cbd5e1", background: sel ? "#2563eb" : "transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:11, fontWeight:900, color:"#fff" }}>
                  {sel && "✓"}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {d.vehicleType && !d.providerId && (
        <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:9, padding:"10px 16px", marginBottom:18, fontSize:13, color:"#b45309" }}>
          ⚠ Select a provider above to continue.
        </div>
      )}

      <div style={{ display:"flex", justifyContent:"space-between" }}>
        <button onClick={onBack} style={IS.btnSec}>← Back</button>
        <button disabled={!ok} onClick={onNext} style={ok ? IS.btnPrim : IS.btnDis}>Review & Confirm →</button>
      </div>
    </div>
  );
}

function SchedConfirm({ member, trip, dispatch, onBack, onSubmit, submitted, token }) {
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError,   setApiError]   = useState("");
  const [rideNum,    setRideNum]     = useState("");
  const vehicle  = VEHICLE_TYPES_LIST.find(v=>v.id===dispatch.vehicleType);
  const provider = SCHED_PROVIDERS.find(p=>p.id===dispatch.providerId);

  const handleConfirm = async () => {
    if (!token) { onSubmit(); return; }
    setApiLoading(true);
    setApiError("");
    try {
      const headers = { "Content-Type":"application/json", "Authorization":`Bearer ${token}` };

      // 1. Create member record
      const mRes  = await fetch(`${API_BASE}/members`, {
        method:"POST", headers,
        body: JSON.stringify({
          first_name:       member.firstName,
          last_name:        member.lastName,
          dob:              member.dob,
          phone:            member.phone,
          payer:            PAYER_MAP[member.payer] || "medicaid",
          member_number:    member.memberId || undefined,
          rides_allowed:    24,
          eligible_through: "2027-12-31",
          special_notes:    member.notes || undefined,
        }),
      });
      const mData = await mRes.json();
      if (!mRes.ok) throw new Error(mData.error || "Could not save member");

      // 2. Create ride
      const rRes  = await fetch(`${API_BASE}/rides`, {
        method:"POST", headers,
        body: JSON.stringify({
          member_id:           mData.id,
          pickup_address:      trip.pickup,
          dropoff_address:     trip.dropoff,
          scheduled_pickup_at: `${trip.date}T${trip.time}:00`,
          purpose:             trip.purpose || undefined,
          vehicle_type:        {"Sedan":"sedan","Wheelchair Van":"wav","Stretcher Van":"stretcher","Bariatric Van":"bariatric","Gurney Van":"other"}[dispatch.vehicleType] || "other",
          payer:               PAYER_MAP[member.payer] || "medicaid",
          return_trip:         (trip.returnTrip==="yes"||trip.returnTrip==="wait") ? "scheduled" : trip.returnTrip==="willcall" ? "will_call" : "none",
          special_notes:       member.notes || undefined,
        }),
      });
      const rData = await rRes.json();
      if (!rRes.ok) throw new Error(rData.error || "Could not create ride");

      setRideNum(rData.ride_number || "");
      onSubmit(rData);
    } catch (err) {
      if (err.message === "Token expired" || err.message === "Invalid token") {
        sessionStorage.removeItem("nemt_token");
        sessionStorage.removeItem("nemt_user");
        window.location.reload();
      } else {
        setApiError(err.message);
      }
    } finally {
      setApiLoading(false);
    }
  };
  const Row = ({label, value}) => (
    <div style={{ display:"flex", justifyContent:"space-between", padding:"9px 0", borderBottom:"1px solid #f1f5f9" }}>
      <span style={{ fontSize:13, color:"#64748b" }}>{label}</span>
      <span style={{ fontSize:13, fontWeight:600, color:"#1e293b", textAlign:"right", maxWidth:"60%" }}>{value || <span style={{ color:"#94a3b8", fontStyle:"italic" }}>—</span>}</span>
    </div>
  );
  if (submitted) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"48px 24px", textAlign:"center" }}>
      <div style={{ width:68, height:68, background:"#f0fdf4", border:"2px solid #bbf7d0", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:34, marginBottom:18 }}>✅</div>
      <h2 style={{ fontSize:24, fontWeight:800, color:"#0f172a", margin:"0 0 6px" }}>Ride Scheduled!</h2>
      <p style={{ color:"#64748b", fontSize:14, margin:"0 0 22px" }}>Confirmation <span style={{ fontFamily:"monospace", fontWeight:700, color:"#1e293b" }}>{rideNum ? `#${rideNum}` : "saved"}</span> sent to provider.</p>
      <div style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:12, padding:"18px 22px", textAlign:"left", width:"100%", maxWidth:380, fontSize:13 }}>
        <p style={{ margin:"0 0 8px" }}><span style={{ color:"#64748b" }}>Member: </span><span style={{ fontWeight:700 }}>{member.firstName} {member.lastName}</span></p>
        <p style={{ margin:"0 0 8px" }}><span style={{ color:"#64748b" }}>Pickup: </span><span style={{ fontWeight:700 }}>{trip.date} at {trip.time}</span></p>
        <p style={{ margin:"0 0 8px" }}><span style={{ color:"#64748b" }}>Provider: </span><span style={{ fontWeight:700 }}>{provider?.name}</span></p>
        <p style={{ margin:0 }}><span style={{ color:"#64748b" }}>Vehicle: </span><span style={{ fontWeight:700 }}>{vehicle?.label}</span></p>
      </div>
    </div>
  );
  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:22, fontWeight:800, color:"#0f172a", margin:"0 0 4px", letterSpacing:"-0.02em" }}>Review & Confirm</h2>
        <p style={{ fontSize:13, color:"#64748b", margin:0 }}>Verify all details before dispatching.</p>
      </div>
      <div style={IS.card}>
        <p style={IS.secTitle}>Member</p>
        <Row label="Name" value={`${member.firstName} ${member.lastName}`}/>
        <Row label="Date of Birth" value={member.dob}/>
        <Row label="Member ID" value={member.memberId}/>
        <Row label="Phone" value={member.phone}/>
        <Row label="Payer" value={member.payer}/>
        {member.notes && <Row label="Notes" value={member.notes}/>}
      </div>
      <div style={IS.card}>
        <p style={IS.secTitle}>Trip</p>
        <Row label="Pickup" value={trip.pickup}/>
        <Row label="Destination" value={trip.dropoff}/>
        <Row label="Date & Time" value={trip.date&&trip.time?`${trip.date} at ${trip.time}`:""}/>
        <Row label="Purpose" value={trip.purpose}/>
        <Row label="Return Trip" value={trip.returnTrip==="yes"?"Yes — scheduled":trip.returnTrip==="willcall"?"Yes — will call":trip.returnTrip==="wait"?"Yes — driver waits":"No"}/>
      </div>
      <div style={IS.card}>
        <p style={IS.secTitle}>Dispatch</p>
        <Row label="Vehicle" value={vehicle?`${vehicle.icon} ${vehicle.label}`:""}/>
        <Row label="Provider" value={provider?.name}/>
        <Row label="Est. Arrival" value={provider?.eta}/>
      </div>
      {apiError && <p style={{ color:"#ef4444", fontSize:13, textAlign:"center", margin:"0 0 12px", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px" }}>{apiError}</p>}
      <div style={{ display:"flex", justifyContent:"space-between" }}>
        <button onClick={onBack} style={IS.btnSec} disabled={apiLoading}>← Back</button>
        <button onClick={handleConfirm} disabled={apiLoading} style={{ padding:"11px 26px", background: apiLoading ? "#94a3b8" : "#16a34a", border:"none", borderRadius:9, fontSize:14, fontWeight:700, color:"#fff", cursor: apiLoading ? "not-allowed" : "pointer" }}>
          {apiLoading ? "Saving…" : "✓ Confirm & Dispatch"}
        </button>
      </div>
    </div>
  );
}

function ModuleScheduler({ token }) {
  const [step,setStep]=useState(0);
  const [submitted,setSubmitted]=useState(false);
  const [member,setMember]=useState({firstName:"",lastName:"",dob:"",memberId:"",phone:"",payer:"",notes:""});
  const [trip,setTrip]=useState({pickup:"",dropoff:"",date:"",time:"",purpose:"",returnTrip:"no",provider:""});
  const [dispatch,setDispatch]=useState({vehicleType:"",providerId:""});
  const cm=(k,v)=>setMember(p=>({...p,[k]:v}));
  const ct=(k,v)=>setTrip(p=>({...p,[k]:v}));
  const cd=(k,v)=>setDispatch(p=>({...p,[k]:v}));
  return (
    <div style={{ background:"#f0f4f8", minHeight:"100%", padding:"32px 28px" }}>
      <div style={{ maxWidth:700, margin:"0 auto" }}>
        <div style={{ marginBottom:24 }}>
          <h1 style={{ fontSize:28, fontWeight:800, color:"#0f172a", margin:"0 0 4px", letterSpacing:"-0.03em" }}>Schedule a Ride</h1>
          <p style={{ fontSize:13, color:"#64748b", margin:0 }}>Complete all steps to dispatch a transportation request.</p>
        </div>
        <SchedStepIndicator current={step}/>
        {step===0&&<SchedMember d={member} ch={cm} onNext={()=>setStep(1)}/>}
        {step===1&&<SchedTrip d={trip} ch={ct} onNext={()=>setStep(2)} onBack={()=>setStep(0)}/>}
        {step===2&&<SchedDispatch d={dispatch} ch={cd} onNext={()=>setStep(3)} onBack={()=>setStep(1)}/>}
        {step===3&&<SchedConfirm member={member} trip={trip} dispatch={dispatch} onBack={()=>setStep(2)} onSubmit={()=>setSubmitted(true)} submitted={submitted} token={token}/>}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MODULE 2 — PROVIDER PORTAL
// ════════════════════════════════════════════════════════════════════
function normalizeRide(r) {
  const dt      = r.scheduled_pickup_at ? new Date(r.scheduled_pickup_at) : null;
  const created = r.created_at          ? new Date(r.created_at)          : null;
  const fmtPayer = v => v ? v.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase()) : "";
  return {
    id:         r.id,                          // UUID — used for all API calls
    rideNumber: r.ride_number || r.id,         // display label (e.g. RD-48821)
    status:     r.status,
    priority:   r.priority || "normal",
    member: {
      name:  r.member_name  || "Unknown",
      phone: r.member_phone || "",
      dob:   "",
      notes: r.member_notes || r.special_notes || "",
    },
    pickup: {
      address: r.pickup_address  || "",
      time: dt ? `${String(dt.getHours()).padStart(2,"0")}:${String(dt.getMinutes()).padStart(2,"0")}` : "",
      date: dt ? dt.toISOString().slice(0,10) : "",
    },
    dropoff: { address: r.dropoff_address || "" },
    purpose:    r.purpose    || "",
    vehicle:    r.vehicle_type || "sedan",
    payer:      fmtPayer(r.payer),
    driver: r.driver_name ? {
      name:    r.driver_name,
      vehicle: [r.vehicle_make, r.vehicle_model].filter(Boolean).join(" ") || "",
      plate:   r.license_plate || "",
      phone:   r.driver_phone  || "",
      eta:     null,
    } : null,
    returnTrip:   r.return_trip === "will_call" ? "willcall" : r.return_trip === "scheduled" ? "yes" : "no",
    receivedMins: created ? Math.max(0, Math.floor((Date.now() - created.getTime()) / 60000)) : 0,
  };
}

function pill(color,children){const c={amber:"bg-amber-50 text-amber-700 border border-amber-200",blue:"bg-blue-50 text-blue-700 border border-blue-200",violet:"bg-violet-50 text-violet-700 border border-violet-200",green:"bg-green-50 text-green-700 border border-green-200",slate:"bg-slate-100 text-slate-500 border border-slate-200",red:"bg-red-50 text-red-700 border border-red-200"}[color]||"";return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${c}`}>{children}</span>;}
function timeAgo(m){if(m<1)return"just now";if(m<60)return`${m}m ago`;return`${Math.floor(m/60)}h ${m%60}m ago`;}
function fmtTime(t){if(!t)return"";const[h,m]=t.split(":").map(Number);return`${h%12||12}:${String(m).padStart(2,"0")} ${h>=12?"PM":"AM"}`;}

function AssignModal({ride,token,onAssign,onClose}){
  const [sel,setSel]=useState(null);
  const [drivers,setDrivers]=useState([]);
  const [drvLoad,setDrvLoad]=useState(true);
  useEffect(()=>{
    fetch(`${API_BASE}/drivers`,{headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`}})
      .then(r=>r.json())
      .then(data=>{
        setDrivers((Array.isArray(data)?data:data.data||[]).map(d=>({
          id:d.id,
          name:`${d.first_name} ${d.last_name}`,
          vehicle:[d.vehicle_make,d.vehicle_model].filter(Boolean).join(" ")||d.vehicle_type,
          plate:d.license_plate||"",
          phone:d.phone||"",
          type:d.vehicle_type,
          status:d.status,
        })));
        setDrvLoad(false);
      })
      .catch(()=>setDrvLoad(false));
  },[]); // eslint-disable-line react-hooks/exhaustive-deps
  const eligible=drivers.filter(d=>d.type===ride.vehicle&&d.status!=="on_trip");
  return(
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{background:"rgba(15,23,42,0.7)"}}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
          <div><p className="font-bold text-slate-800">Assign Driver</p><p className="text-xs text-slate-500 mt-0.5">{ride.rideNumber||ride.id} · {VEHICLE_ICONS[ride.vehicle]} {VEHICLE_LABELS[ride.vehicle]}</p></div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl font-bold">×</button>
        </div>
        <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
          {drvLoad&&<p className="text-center text-sm text-slate-400 py-4">Loading drivers…</p>}
          {!drvLoad&&eligible.length===0&&<p className="text-center text-sm text-slate-400 py-4">No available drivers for this vehicle type.</p>}
          {eligible.map(d=>(
            <label key={d.id} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${sel===d.id?"border-blue-500 bg-blue-50":"border-slate-200 hover:border-slate-300"}`}>
              <input type="radio" className="sr-only" checked={sel===d.id} onChange={()=>setSel(d.id)}/>
              <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-600 text-sm">{d.name.split(" ").map(n=>n[0]).join("")}</div>
              <div className="flex-1"><p className="text-sm font-semibold text-slate-800">{d.name}</p><p className="text-xs text-slate-500">{d.vehicle} · {d.plate}</p></div>
              {pill(d.status==="available"?"green":"amber",d.status==="available"?"Available":"On Break")}
              {sel===d.id&&<span className="text-blue-600 font-bold">✓</span>}
            </label>
          ))}
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition">Cancel</button>
          <button disabled={!sel} onClick={()=>onAssign(ride.id,drivers.find(d=>d.id===sel))} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-semibold rounded-lg transition">Confirm Assignment</button>
        </div>
      </div>
    </div>
  );
}

function RideCard({ride,onAssign,onStatusChange}){
  const [exp,setExp]=useState(false);
  return(
    <div className={`bg-white rounded-xl border shadow-sm ${ride.priority==="urgent"&&ride.status==="new"?"border-red-200 ring-1 ring-red-100":"border-slate-200"}`}>
      <div className="px-4 pt-4 pb-3 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-xl flex-shrink-0">{VEHICLE_ICONS[ride.vehicle]}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap"><span className="font-bold text-slate-800 text-sm">{ride.member.name}</span>{ride.priority==="urgent"&&pill("red","⚡ Urgent")}</div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-xs font-mono text-slate-400">{ride.rideNumber||ride.id}</span>
            <DarkStatusPill status={ride.status}/>
            <span className="text-xs text-slate-400">{timeAgo(ride.receivedMins)}</span>
          </div>
        </div>
        <button onClick={()=>setExp(e=>!e)} className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1 rounded hover:bg-slate-100 transition">{exp?"▲":"▼"}</button>
      </div>
      <div className="px-4 pb-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div className="col-span-2 flex items-start gap-1.5 text-slate-600"><span className="text-slate-400">📍</span><span className="truncate">{ride.pickup.address}</span></div>
        <div className="col-span-2 flex items-start gap-1.5 text-slate-600"><span className="text-slate-400">🏁</span><span className="truncate">{ride.dropoff.address}</span></div>
        <div className="flex items-center gap-1.5 text-slate-500"><span>🕐</span><span className="font-medium text-slate-700">{fmtTime(ride.pickup.time)}</span></div>
        <div className="flex items-center gap-1.5 text-slate-500"><span>{VEHICLE_ICONS[ride.vehicle]}</span><span>{VEHICLE_LABELS[ride.vehicle]}</span></div>
      </div>
      {exp&&(
        <div className="border-t border-slate-100 mx-4 pt-3 pb-3 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div><p className="text-slate-400 font-semibold uppercase tracking-wide mb-1">Member</p><p className="text-slate-700">{ride.member.phone}</p><p className="text-slate-500">DOB: {ride.member.dob}</p>{ride.member.notes&&<p className="mt-1 text-amber-700 bg-amber-50 rounded px-2 py-1">{ride.member.notes}</p>}</div>
            <div><p className="text-slate-400 font-semibold uppercase tracking-wide mb-1">Trip Info</p><p className="text-slate-700">{ride.purpose}</p><p className="text-slate-500">Payer: {ride.payer}</p></div>
          </div>
          {ride.driver&&(<div className="bg-slate-50 rounded-lg p-3 text-xs"><p className="text-slate-400 font-semibold uppercase tracking-wide mb-1.5">Driver</p><div className="flex items-center gap-2"><div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xs">{ride.driver.name.split(" ").map(n=>n[0]).join("")}</div><div><p className="font-semibold text-slate-800">{ride.driver.name}</p><p className="text-slate-500">{ride.driver.vehicle} · {ride.driver.plate}</p></div>{ride.driver.eta&&<div className="ml-auto">{pill("violet",`🕐 ETA ${ride.driver.eta}`)}</div>}</div></div>)}
        </div>
      )}
      <div className="border-t border-slate-100 px-4 py-3 flex gap-2 flex-wrap">
        {ride.status==="new"&&<><button onClick={()=>onAssign(ride)} className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition">Assign Driver</button><button onClick={()=>onStatusChange(ride.id,"cancelled")} className="px-3 py-2 text-slate-500 hover:bg-slate-100 text-xs font-medium rounded-lg transition border border-slate-200">Cancel</button></>}
        {ride.status==="assigned"&&<><button onClick={()=>onStatusChange(ride.id,"en_route")} className="flex-1 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold rounded-lg transition">Mark En Route</button><button onClick={()=>onAssign(ride)} className="px-3 py-2 text-slate-500 hover:bg-slate-100 text-xs font-medium rounded-lg transition border border-slate-200">Reassign</button></>}
        {ride.status==="en_route"&&<button onClick={()=>onStatusChange(ride.id,"completed")} className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition">Mark Completed</button>}
        {(ride.status==="completed"||ride.status==="cancelled")&&<span className="text-xs text-slate-400 py-2">{ride.status==="completed"?"✓ Trip complete":"✕ Cancelled"}</span>}
      </div>
    </div>
  );
}

function ModuleProvider({ token }) {
  const [rides,setRides]           = useState([]);
  const [loading,setLoading]       = useState(true);
  const [fetchErr,setFetchErr]     = useState("");
  const [assignTarget,setAssignTarget] = useState(null);
  const [filter,setFilter]         = useState("active");
  const [search,setSearch]         = useState("");

  const hdrs = { "Content-Type":"application/json", "Authorization":`Bearer ${token}` };

  const loadRides = () => {
    setLoading(true);
    fetch(`${API_BASE}/rides?limit=100`, { headers: hdrs })
      .then(r => r.json())
      .then(data => { setRides((data.data||[]).map(normalizeRide)); setLoading(false); })
      .catch(err => { setFetchErr(err.message); setLoading(false); });
  };

  useEffect(() => { loadRides(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusChange = async (rideId, newStatus) => {
    setRides(p => p.map(r => r.id===rideId ? {...r, status:newStatus} : r));
    try {
      const res = await fetch(`${API_BASE}/rides/${rideId}/status`, {
        method:"PATCH", headers: hdrs, body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error||"Update failed"); }
    } catch(err) { setFetchErr(err.message); loadRides(); }
  };

  const handleAssign = async (rideId, driver) => {
    setRides(p => p.map(r => r.id===rideId ? {...r, status:"assigned", driver:{name:driver.name,vehicle:driver.vehicle,plate:driver.plate,phone:driver.phone,eta:"12 min"}} : r));
    setAssignTarget(null);
    try {
      await fetch(`${API_BASE}/rides/${rideId}/status`, {
        method:"PATCH", headers: hdrs, body: JSON.stringify({ status:"assigned" }),
      });
    } catch(err) { setFetchErr(err.message); }
  };

  const counts=rides.reduce((a,r)=>{a[r.status]=(a[r.status]||0)+1;return a;},{});
  const newCount=counts.new||0;
  const filtered=rides.filter(r=>{
    const mf=filter==="all"?true:filter==="active"?["new","assigned","en_route"].includes(r.status):r.status===filter;
    const ms=!search||r.member.name.toLowerCase().includes(search.toLowerCase())||(r.rideNumber||"").toLowerCase().includes(search.toLowerCase());
    return mf&&ms;
  });
  return(
    <div className="bg-slate-50 min-h-full p-6">
      {assignTarget&&<AssignModal ride={assignTarget} token={token} onAssign={(rid,drv)=>handleAssign(rid,drv)} onClose={()=>setAssignTarget(null)}/>}
      <div className="flex items-center justify-between mb-5">
        <div><h1 className="text-xl font-bold text-slate-900">Dispatch Board</h1><p className="text-sm text-slate-500 mt-0.5">Today · {new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}</p></div>
        <div className="flex items-center gap-2">
          {newCount>0&&<div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-3 py-1"><span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse inline-block"/><span className="text-xs font-semibold text-amber-700">{newCount} new</span></div>}
          <button onClick={loadRides} className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg hover:bg-white border border-slate-200 transition">↻ Refresh</button>
        </div>
      </div>
      {fetchErr&&<div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2 flex justify-between"><span>{fetchErr}</span><button onClick={()=>setFetchErr("")} className="font-bold">×</button></div>}
      {loading&&<div className="text-center py-16 text-slate-400 text-sm">Loading rides…</div>}
      {!loading&&<>
        <div className="grid grid-cols-4 gap-3 mb-5">
          {[{label:"New",val:counts.new||0,color:"#f59e0b",bg:"#fffbeb"},{label:"Assigned",val:counts.assigned||0,color:"#3b82f6",bg:"#eff6ff"},{label:"En Route",val:counts.en_route||0,color:"#8b5cf6",bg:"#f5f3ff"},{label:"Completed",val:counts.completed||0,color:"#22c55e",bg:"#f0fdf4"}].map(s=>(
            <div key={s.label} className="rounded-xl p-3 border border-slate-200" style={{background:s.bg}}><p className="text-2xl font-bold" style={{color:s.color}}>{s.val}</p><p className="text-xs font-medium text-slate-600 mt-0.5">{s.label}</p></div>
          ))}
        </div>
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="flex bg-white border border-slate-200 rounded-lg p-0.5 gap-0.5">
            {[["active","Active"],["new","New"],["assigned","Assigned"],["en_route","En Route"],["completed","Done"],["all","All"]].map(([k,l])=>(
              <button key={k} onClick={()=>setFilter(k)} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${filter===k?"bg-blue-600 text-white":"text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
          <input className="flex-1 min-w-32 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition" placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <div className="space-y-3">
          {filtered.map(r=><RideCard key={r.id} ride={r} onAssign={setAssignTarget} onStatusChange={handleStatusChange}/>)}
          {filtered.length===0&&<div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400 text-sm">No rides match this filter.</div>}
        </div>
      </>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MODULE 3 — ADMIN DASHBOARD
// ════════════════════════════════════════════════════════════════════
const ADMIN_PROVIDERS=[
  {id:"p1",name:"CityRide Medical",region:"McAllen Central",drivers:8,active:3,completed:12,pending:2,onTime:94,rating:4.8,vehicles:{sedan:4,wav:2,stretcher:2}},
  {id:"p2",name:"Metro Access Transport",region:"McAllen North",drivers:12,active:5,completed:18,pending:3,onTime:89,rating:4.6,vehicles:{sedan:5,wav:3,stretcher:2,bariatric:2}},
  {id:"p3",name:"QuickCare Rides",region:"Edinburg",drivers:6,active:2,completed:9,pending:1,onTime:97,rating:4.9,vehicles:{sedan:4,wav:2}},
  {id:"p4",name:"AllCare Medical Transit",region:"Mission / Pharr",drivers:9,active:4,completed:14,pending:4,onTime:86,rating:4.5,vehicles:{sedan:4,wav:3,stretcher:2}},
  {id:"p5",name:"ValleyStar Transport",region:"Harlingen",drivers:5,active:1,completed:6,pending:0,onTime:92,rating:4.7,vehicles:{sedan:3,wav:2}},
];
const HOURLY=[{h:"6a",r:2},{h:"7a",r:8},{h:"8a",r:14},{h:"9a",r:19},{h:"10a",r:16},{h:"11a",r:13},{h:"12p",r:10},{h:"1p",r:15},{h:"2p",r:18},{h:"3p",r:21},{h:"4p",r:17},{h:"5p",r:9}];
const WEEKLY=[{d:"Mon",r:87,c:82},{d:"Tue",r:94,c:90},{d:"Wed",r:78,c:75},{d:"Thu",r:102,c:96},{d:"Fri",r:115,c:108},{d:"Sat",r:62,c:61},{d:"Sun",r:41,c:40}];
const PAYER_MIX=[{name:"Medicaid",pct:48,color:"#38bdf8"},{name:"Medicare",pct:27,color:"#818cf8"},{name:"Health Plan",pct:17,color:"#34d399"},{name:"Private Pay",pct:8,color:"#fb923c"}];
const RIDE_FEED=[
  {id:"RD-48835",member:"Patricia Chen",provider:"QuickCare Rides",vehicle:"sedan",status:"new",time:"2:14 PM",purpose:"Primary Care"},
  {id:"RD-48834",member:"Harold Trevino",provider:"CityRide Medical",vehicle:"wav",status:"en_route",time:"2:10 PM",purpose:"Dialysis"},
  {id:"RD-48833",member:"Yolanda Garza",provider:"Metro Access",vehicle:"sedan",status:"assigned",time:"2:08 PM",purpose:"Specialist"},
  {id:"RD-48832",member:"Robert Blum",provider:"AllCare Transit",vehicle:"stretcher",status:"en_route",time:"1:55 PM",purpose:"Hospital Discharge"},
  {id:"RD-48831",member:"Maria Espinoza",provider:"CityRide Medical",vehicle:"sedan",status:"completed",time:"1:40 PM",purpose:"Lab / Imaging"},
];
const AdTip=({active,payload,label})=>{if(!active||!payload?.length)return null;return(<div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:8,padding:"6px 12px",fontSize:12,color:"#e2e8f0"}}><p style={{color:"#94a3b8",marginBottom:2}}>{label}</p><p style={{fontWeight:700}}>{payload[0].value} rides</p></div>);};
function DonutChart({data,size=110,thickness=20}){const r=(size-thickness)/2,cx=size/2,cy=size/2,circ=2*Math.PI*r,total=data.reduce((a,d)=>a+d.pct,0);let off=0;const gap=2;return(<svg width={size} height={size}>{data.map((d,i)=>{const len=(d.pct/total)*(circ-data.length*gap);const seg=(<circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={d.color} strokeWidth={thickness} strokeDasharray={`${len} ${circ-len}`} strokeDashoffset={-off} style={{transform:"rotate(-90deg)",transformOrigin:`${cx}px ${cy}px`}}/>);off+=len+gap;return seg;})}<text x={cx} y={cy-5} textAnchor="middle" fill="#f1f5f9" fontSize={18} fontWeight={800}>{total}</text><text x={cx} y={cy+11} textAnchor="middle" fill="#475569" fontSize={9}>total %</text></svg>);}

function ModuleAdmin(){
  const [selProv,setSelProv]=useState(null);
  const totalActive=ADMIN_PROVIDERS.reduce((a,p)=>a+p.active,0);
  const totalDone=ADMIN_PROVIDERS.reduce((a,p)=>a+p.completed,0);
  const totalPend=ADMIN_PROVIDERS.reduce((a,p)=>a+p.pending,0);
  const avgOT=Math.round(ADMIN_PROVIDERS.reduce((a,p)=>a+p.onTime,0)/ADMIN_PROVIDERS.length);
  const S=({label,value,sub,accent,icon,trend})=>(<div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:14,padding:"16px 18px",position:"relative",overflow:"hidden"}}><div style={{position:"absolute",top:0,right:0,width:48,height:48,borderRadius:"0 14px 0 50px",background:accent+"18",display:"flex",alignItems:"flex-start",justifyContent:"flex-end",padding:"8px 10px",fontSize:16}}>{icon}</div><p style={{fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{label}</p><p style={{fontSize:28,fontWeight:800,color:"#f1f5f9",lineHeight:1,marginBottom:4}}>{value}</p><div style={{display:"flex",alignItems:"center",gap:6}}>{trend&&<span style={{fontSize:11,fontWeight:700,color:trend>0?"#4ade80":"#f87171"}}>{trend>0?"▲":"▼"}{Math.abs(trend)}%</span>}<span style={{fontSize:11,color:"#475569"}}>{sub}</span></div></div>);
  return(
    <div style={{background:"#080e1a",minHeight:"100%",color:"#e2e8f0",padding:"24px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div><h1 style={{fontSize:20,fontWeight:800,color:"#f1f5f9",margin:0,letterSpacing:"-0.03em"}}>Operations Overview</h1><p style={{fontSize:12,color:"#475569",marginTop:4}}>{new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})}</p></div>
        <select style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:8,padding:"5px 10px",fontSize:11,color:"#94a3b8",cursor:"pointer"}}>
          <option>All Regions</option><option>McAllen</option><option>Edinburg</option>
        </select>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:20}}>
        <S label="Active Rides" value={totalActive} sub="all providers" accent="#38bdf8" icon="🚗" trend={12}/>
        <S label="Completed Today" value={totalDone} sub="of 59 scheduled" accent="#4ade80" icon="✅" trend={8}/>
        <S label="Pending Dispatch" value={totalPend} sub="awaiting assignment" accent="#fb923c" icon="⏳"/>
        <S label="Avg On-Time" value={avgOT+"%"} sub="network average" accent="#818cf8" icon="⏱" trend={-2}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 280px",gap:14,marginBottom:14}}>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:14,padding:"16px 18px"}}>
            <p style={{fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.09em",marginBottom:10}}>Rides by Hour — Today</p>
            <ResponsiveContainer width="100%" height={110}><BarChart data={HOURLY} barSize={16}><XAxis dataKey="h" tick={{fill:"#475569",fontSize:9}} axisLine={false} tickLine={false}/><YAxis hide/><Tooltip content={<AdTip/>} cursor={{fill:"#1e293b"}}/><Bar dataKey="r" radius={[4,4,0,0]}>{HOURLY.map((e,i)=><Cell key={i} fill={e.h==="3p"?"#38bdf8":"#1e3a5f"}/>)}</Bar></BarChart></ResponsiveContainer>
          </div>
          <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:14,overflow:"hidden"}}>
            <div style={{padding:"12px 14px 8px",borderBottom:"1px solid #1e293b"}}><p style={{fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.09em",margin:0}}>Provider Network</p></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 70px 55px 55px 65px 55px",padding:"6px 14px",borderBottom:"1px solid #1e293b"}}>
              {["Provider","Active","Done","Queue","On-Time","Rating"].map(h=><p key={h} style={{fontSize:9,fontWeight:700,color:"#334155",textTransform:"uppercase",letterSpacing:"0.07em",margin:0,textAlign:h==="Provider"?"left":"center"}}>{h}</p>)}
            </div>
            {ADMIN_PROVIDERS.map(p=>{
              const otc=p.onTime>=93?"#4ade80":p.onTime>=88?"#facc15":"#f87171";
              return(
                <div key={p.id} onClick={()=>setSelProv(selProv?.id===p.id?null:p)} style={{display:"grid",gridTemplateColumns:"1fr 70px 55px 55px 65px 55px",alignItems:"center",padding:"9px 14px",cursor:"pointer",borderBottom:"1px solid #1e293b",background:selProv?.id===p.id?"#1e293b":"transparent"}}>
                  <div><p style={{fontSize:12,fontWeight:600,color:"#e2e8f0",margin:0}}>{p.name}</p><p style={{fontSize:10,color:"#475569",marginTop:1}}>{p.region}</p></div>
                  <p style={{textAlign:"center",fontSize:12,fontWeight:700,color:"#38bdf8",margin:0}}>{p.active}<span style={{color:"#475569"}}>/{p.drivers}</span></p>
                  <p style={{textAlign:"center",fontSize:12,fontWeight:700,color:"#e2e8f0",margin:0}}>{p.completed}</p>
                  <p style={{textAlign:"center",fontSize:12,fontWeight:700,color:"#fb923c",margin:0}}>{p.pending}</p>
                  <p style={{textAlign:"center",fontSize:12,fontWeight:700,color:otc,margin:0}}>{p.onTime}%</p>
                  <p style={{textAlign:"center",fontSize:12,color:"#fbbf24",margin:0}}>★ {p.rating}</p>
                </div>
              );
            })}
            {selProv&&(
              <div style={{padding:"14px 16px",borderTop:"1px solid #334155",background:"#0a1628"}}>
                <p style={{fontSize:12,fontWeight:700,color:"#f1f5f9",margin:0,marginBottom:8}}>{selProv.name} — Fleet</p>
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  {Object.entries(selProv.vehicles).map(([t,c])=><div key={t} style={{background:"#1e293b",borderRadius:8,padding:"8px 12px",fontSize:11,color:"#94a3b8"}}>{VEHICLE_ICONS[t]} {c} {VEHICLE_LABELS[t]}</div>)}
                </div>
              </div>
            )}
          </div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:14,padding:"14px"}}>
            <p style={{fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.09em",marginBottom:10}}>Payer Mix</p>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <DonutChart data={PAYER_MIX}/>
              <div style={{flex:1,display:"flex",flexDirection:"column",gap:7}}>
                {PAYER_MIX.map(p=><div key={p.name} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}><div style={{display:"flex",alignItems:"center",gap:5}}><span style={{width:7,height:7,borderRadius:2,background:p.color,display:"inline-block"}}/><span style={{fontSize:11,color:"#94a3b8"}}>{p.name}</span></div><span style={{fontSize:12,fontWeight:700,color:"#e2e8f0"}}>{p.pct}%</span></div>)}
              </div>
            </div>
          </div>
          <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:14,padding:"14px"}}>
            <p style={{fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.09em",marginBottom:10}}>Weekly Volume</p>
            <ResponsiveContainer width="100%" height={80}><LineChart data={WEEKLY}><XAxis dataKey="d" tick={{fill:"#475569",fontSize:9}} axisLine={false} tickLine={false}/><YAxis hide/><Tooltip content={<AdTip/>}/><Line type="monotone" dataKey="r" stroke="#38bdf8" strokeWidth={2} dot={false}/><Line type="monotone" dataKey="c" stroke="#4ade80" strokeWidth={2} dot={false} strokeDasharray="4 2"/></LineChart></ResponsiveContainer>
            <div style={{display:"flex",gap:10,marginTop:6,fontSize:10,color:"#475569"}}><span><span style={{color:"#38bdf8"}}>—</span> Scheduled</span><span><span style={{color:"#4ade80"}}>- -</span> Completed</span></div>
          </div>
          <div style={{background:"#3b82f6",borderRadius:14,padding:"14px",color:"#fff"}}>
            <p style={{fontSize:10,fontWeight:700,opacity:0.8,textTransform:"uppercase",letterSpacing:"0.07em",margin:0,marginBottom:4}}>Today's Rides</p>
            <p style={{fontSize:28,fontWeight:800,margin:0}}>59</p>
            <p style={{fontSize:11,opacity:0.7,marginTop:2}}>{totalDone} completed · {totalActive} active</p>
          </div>
        </div>
      </div>
      <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:14,overflow:"hidden"}}>
        <div style={{padding:"12px 18px",borderBottom:"1px solid #1e293b",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}><p style={{fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.09em",margin:0}}>Live Ride Feed</p><span style={{width:6,height:6,borderRadius:"50%",background:"#4ade80",display:"inline-block",boxShadow:"0 0 6px #4ade80"}}/></div>
        </div>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead><tr style={{borderBottom:"1px solid #1e293b"}}>{["Ride ID","Member","Provider","Vehicle","Purpose","Status","Time"].map(h=><th key={h} style={{padding:"7px 14px",textAlign:"left",fontSize:9,fontWeight:700,color:"#334155",textTransform:"uppercase",letterSpacing:"0.07em",whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
          <tbody>{RIDE_FEED.map((r,i)=>(
            <tr key={r.id} style={{borderBottom:"1px solid #1e293b",background:i%2===0?"transparent":"#0a1628"}}>
              <td style={{padding:"9px 14px",fontFamily:"monospace",fontSize:11,color:"#38bdf8"}}>{r.id}</td>
              <td style={{padding:"9px 14px",fontWeight:600,color:"#e2e8f0"}}>{r.member}</td>
              <td style={{padding:"9px 14px",color:"#64748b"}}>{r.provider}</td>
              <td style={{padding:"9px 14px"}}>{VEHICLE_ICONS[r.vehicle]}</td>
              <td style={{padding:"9px 14px",color:"#64748b"}}>{r.purpose}</td>
              <td style={{padding:"9px 14px"}}><DarkStatusPill status={r.status}/></td>
              <td style={{padding:"9px 14px",color:"#475569",fontFamily:"monospace",fontSize:11}}>{r.time}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MODULE 4 — MEMBER PROFILE
// ════════════════════════════════════════════════════════════════════
function normalizeMember(r){
  const ridesUsed=r.rides_used||0,ridesAllowed=r.rides_allowed||0;
  return{
    id:r.member_number||r.id,
    uuid:r.id,
    firstName:r.first_name||"",
    lastName:r.last_name||"",
    dob:r.dob?String(r.dob).slice(0,10):"",
    phone:r.phone||"",
    email:r.email||"",
    address:[r.address,r.city,r.state,r.zip].filter(Boolean).join(", "),
    payer:r.payer?r.payer.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase()):"",
    planId:r.plan_id||"",
    eligibleThrough:r.eligible_through?String(r.eligible_through).slice(0,10):"",
    ridesAllowed,ridesUsed,
    ridesRemaining:Math.max(0,ridesAllowed-ridesUsed),
    conditions:Array.isArray(r.conditions)?r.conditions:[],
    mobility:r.mobility_aid||"Ambulatory",
    language:r.language||"English",
    notes:r.special_notes||"",
    status:r.active?"active":"inactive",
    photo:`${(r.first_name||"?")[0]}${(r.last_name||"?")[0]}`.toUpperCase(),
  };
}
function MAvatar({initials,size=44}){return(<div style={{width:size,height:size,borderRadius:"50%",background:"linear-gradient(135deg,#3b82f6,#06b6d4)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.3,fontWeight:800,color:"#fff",flexShrink:0}}>{initials}</div>);}
function UsageBar({used,total}){const pct=Math.round((used/total)*100);const c=pct>85?"#f87171":pct>60?"#fbbf24":"#4ade80";return(<div><div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{fontSize:11,color:"#94a3b8"}}>{used} of {total}</span><span style={{fontSize:11,fontWeight:700,color:c}}>{pct}%</span></div><div style={{height:5,background:"#1e293b",borderRadius:99,overflow:"hidden"}}><div style={{height:"100%",width:`${pct}%`,background:c,borderRadius:99}}/></div></div>);}

function ModuleMembers({token}){
  const [members,setMembers]=useState([]);
  const [memLoad,setMemLoad]=useState(true);
  const [memErr,setMemErr]=useState("");
  const [search,setSearch]=useState("");
  const [sel,setSel]=useState(null);
  const [tab,setTab]=useState("profile");
  const [trips,setTrips]=useState([]);
  const [tripsLoad,setTripsLoad]=useState(false);
  const hdrs={"Content-Type":"application/json","Authorization":`Bearer ${token}`};
  useEffect(()=>{
    fetch(`${API_BASE}/members?limit=100`,{headers:hdrs})
      .then(r=>r.json())
      .then(data=>{const rows=(data.data||[]).map(normalizeMember);setMembers(rows);if(rows.length)setSel(rows[0]);setMemLoad(false);})
      .catch(err=>{setMemErr(err.message);setMemLoad(false);});
  },[]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{
    if(!sel||tab!=="trips")return;
    setTripsLoad(true);setTrips([]);
    fetch(`${API_BASE}/members/${sel.uuid}/rides?limit=20`,{headers:hdrs})
      .then(r=>r.json())
      .then(data=>{
        setTrips((data.data||[]).map(r=>{
          const dt=r.scheduled_pickup_at?new Date(r.scheduled_pickup_at):null;
          return{id:r.ride_number||r.id,date:dt?dt.toISOString().slice(0,10):"",time:dt?`${String(dt.getHours()).padStart(2,"0")}:${String(dt.getMinutes()).padStart(2,"0")}`:"",purpose:r.purpose||"",vehicle:r.vehicle_type||"sedan",provider:r.provider_name||"",driver:r.driver_name||null,status:r.status};
        }));
        setTripsLoad(false);
      })
      .catch(()=>setTripsLoad(false));
  },[sel,tab]); // eslint-disable-line react-hooks/exhaustive-deps
  if(memLoad)return<div style={{background:"#080e1a",minHeight:"100%",display:"flex",alignItems:"center",justifyContent:"center",color:"#475569",fontSize:13}}>Loading members…</div>;
  if(memErr)return<div style={{background:"#080e1a",minHeight:"100%",display:"flex",alignItems:"center",justifyContent:"center",color:"#f87171",fontSize:13}}>⚠ {memErr}</div>;
  const filtered=members.filter(m=>!search||`${m.firstName} ${m.lastName} ${m.id}`.toLowerCase().includes(search.toLowerCase()));
  const m=sel;
  return(
    <div style={{display:"flex",height:"100%",background:"#080e1a",color:"#e2e8f0"}}>
      <div style={{width:260,borderRight:"1px solid #1e293b",background:"#0a1628",display:"flex",flexDirection:"column",flexShrink:0}}>
        <div style={{padding:"10px 10px 6px"}}><input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search members…" style={{width:"100%",background:"#0f172a",border:"1px solid #1e293b",borderRadius:7,padding:"6px 10px",fontSize:12,color:"#e2e8f0",outline:"none",boxSizing:"border-box"}}/></div>
        <div style={{flex:1,overflowY:"auto"}}>
          {filtered.map(mem=>(
            <div key={mem.id} onClick={()=>setSel(mem)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",cursor:"pointer",borderBottom:"1px solid #1e293b",background:sel?.id===mem.id?"#1e3a5f":"transparent",borderLeft:sel?.id===mem.id?"3px solid #3b82f6":"3px solid transparent"}}>
              <MAvatar initials={mem.photo} size={34}/>
              <div style={{flex:1,minWidth:0}}><p style={{fontSize:12,fontWeight:700,color:"#f1f5f9",margin:0}}>{mem.firstName} {mem.lastName}</p><p style={{fontSize:10,color:"#475569",margin:0,marginTop:1,fontFamily:"monospace"}}>{mem.id}</p></div>
              <p style={{fontSize:11,fontWeight:700,color:mem.ridesRemaining<4?"#f87171":"#4ade80",margin:0}}>{mem.ridesRemaining} left</p>
            </div>
          ))}
        </div>
      </div>
      {m&&(
        <div style={{flex:1,overflowY:"auto",padding:"22px 24px"}}>
          <div style={{display:"flex",alignItems:"flex-start",gap:14,marginBottom:20}}>
            <MAvatar initials={m.photo} size={52}/>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <h1 style={{fontSize:20,fontWeight:800,color:"#f1f5f9",margin:0}}>{m.firstName} {m.lastName}</h1>
                <span style={{background:"#052e16",color:"#4ade80",borderRadius:99,padding:"2px 9px",fontSize:11,fontWeight:700}}>● Active</span>
              </div>
              <div style={{display:"flex",gap:14,marginTop:5,flexWrap:"wrap"}}>
                <span style={{fontSize:11,color:"#475569",fontFamily:"monospace"}}>{m.id}</span>
                <span style={{fontSize:11,color:"#475569"}}>DOB: {m.dob}</span>
                <span style={{fontSize:11,color:"#475569"}}>{m.payer}</span>
              </div>
            </div>
            <button style={{background:"#3b82f6",border:"none",borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,color:"#fff",cursor:"pointer",flexShrink:0}}>+ Schedule Ride</button>
          </div>
          <div style={{display:"flex",gap:2,borderBottom:"1px solid #1e293b",marginBottom:18}}>
            {["profile","trips","notes"].map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:"7px 16px",fontSize:12,fontWeight:600,border:"none",cursor:"pointer",background:"transparent",color:tab===t?"#38bdf8":"#475569",borderBottom:tab===t?"2px solid #38bdf8":"2px solid transparent"}}>{t==="trips"?"Ride History":t.charAt(0).toUpperCase()+t.slice(1)}</button>)}
          </div>
          {tab==="profile"&&(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              {[
                {title:"Contact & Address",rows:[["Phone",m.phone],["Email",m.email||"—"],["Address",m.address],["Language",m.language],["Mobility",m.mobility]]},
                {title:"Insurance",rows:[["Payer",m.payer],["Plan ID",m.planId],["Eligible Through",m.eligibleThrough]]},
              ].map(card=>(
                <div key={card.title} style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:12,padding:"16px"}}>
                  <p style={{fontSize:10,fontWeight:700,color:"#334155",textTransform:"uppercase",letterSpacing:"0.09em",marginBottom:12}}>{card.title}</p>
                  {card.rows.map(([l,v])=><div key={l} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #1e293b"}}><span style={{fontSize:11,color:"#475569"}}>{l}</span><span style={{fontSize:11,color:"#e2e8f0",fontWeight:500,textAlign:"right",maxWidth:"60%"}}>{v}</span></div>)}
                  {card.title==="Insurance"&&<div style={{marginTop:12}}><p style={{fontSize:10,fontWeight:700,color:"#334155",textTransform:"uppercase",letterSpacing:"0.09em",marginBottom:8}}>Authorization</p><UsageBar used={m.ridesUsed} total={m.ridesAllowed}/><p style={{fontSize:11,color:"#4ade80",fontWeight:700,marginTop:8}}>{m.ridesRemaining} rides remaining</p></div>}
                </div>
              ))}
              <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:12,padding:"16px"}}>
                <p style={{fontSize:10,fontWeight:700,color:"#334155",textTransform:"uppercase",letterSpacing:"0.09em",marginBottom:10}}>Conditions</p>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>{m.conditions.map(c=><span key={c} style={{background:"#1e293b",color:"#94a3b8",borderRadius:7,padding:"4px 9px",fontSize:11}}>{c}</span>)}</div>
              </div>
              <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:12,padding:"16px"}}>
                <p style={{fontSize:10,fontWeight:700,color:"#334155",textTransform:"uppercase",letterSpacing:"0.09em",marginBottom:10}}>Special Instructions</p>
                {m.notes?<p style={{fontSize:12,color:"#fbbf24",background:"#1c1100",borderRadius:8,padding:"9px 11px",lineHeight:1.6}}>⚠️ {m.notes}</p>:<p style={{fontSize:12,color:"#334155",fontStyle:"italic"}}>None on file.</p>}
              </div>
            </div>
          )}
          {tab==="trips"&&(
            <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:12,overflow:"hidden"}}>
              {tripsLoad&&<p style={{textAlign:"center",padding:"20px",color:"#475569",fontSize:13}}>Loading ride history…</p>}
              {!tripsLoad&&trips.length===0&&<p style={{textAlign:"center",padding:"20px",color:"#334155",fontSize:13}}>No rides on record.</p>}
              {!tripsLoad&&trips.length>0&&<table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead><tr style={{borderBottom:"1px solid #1e293b"}}>{["Date","Time","Purpose","Vehicle","Provider","Driver","Status"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:9,fontWeight:700,color:"#334155",textTransform:"uppercase",letterSpacing:"0.07em"}}>{h}</th>)}</tr></thead>
                <tbody>{trips.map((t,i)=>(<tr key={t.id} style={{borderBottom:"1px solid #1e293b",background:i%2===0?"#0a1628":"transparent"}}>
                  <td style={{padding:"9px 12px",color:"#94a3b8",fontFamily:"monospace",fontSize:11}}>{t.date}</td>
                  <td style={{padding:"9px 12px",color:"#94a3b8",fontFamily:"monospace",fontSize:11}}>{t.time}</td>
                  <td style={{padding:"9px 12px",color:"#e2e8f0",fontWeight:600}}>{t.purpose}</td>
                  <td style={{padding:"9px 12px"}}>{VEHICLE_ICONS[t.vehicle]}</td>
                  <td style={{padding:"9px 12px",color:"#64748b"}}>{t.provider}</td>
                  <td style={{padding:"9px 12px",color:"#64748b"}}>{t.driver||"—"}</td>
                  <td style={{padding:"9px 12px"}}><DarkStatusPill status={t.status}/></td>
                </tr>))}</tbody>
              </table>}
            </div>
          )}
          {tab==="notes"&&(
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:12,padding:"16px"}}><p style={{fontSize:10,fontWeight:700,color:"#334155",textTransform:"uppercase",letterSpacing:"0.09em",marginBottom:10}}>Add Note</p><textarea rows={3} placeholder="Enter case note…" style={{width:"100%",background:"#0a1628",border:"1px solid #1e293b",borderRadius:7,padding:"9px 12px",fontSize:12,color:"#e2e8f0",resize:"vertical",outline:"none",boxSizing:"border-box"}}/><div style={{display:"flex",justifyContent:"flex-end",marginTop:8}}><button style={{background:"#3b82f6",border:"none",borderRadius:7,padding:"6px 16px",fontSize:12,fontWeight:700,color:"#fff",cursor:"pointer"}}>Save Note</button></div></div>
              <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:12,padding:"16px"}}><p style={{fontSize:10,fontWeight:700,color:"#334155",textTransform:"uppercase",letterSpacing:"0.09em",marginBottom:12}}>Note History</p>{[{author:"J. Martinez",date:"2026-03-29",note:"Member confirmed dialysis ride for 4/1. WAV required."},{author:"S. Rodriguez",date:"2026-03-20",note:"Ride RD-48102 cancelled — member in hospital."}].map((n,i)=><div key={i} style={{borderBottom:"1px solid #1e293b",paddingBottom:10,marginBottom:10}}><div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{fontSize:12,fontWeight:700,color:"#94a3b8"}}>{n.author}</span><span style={{fontSize:10,color:"#334155",fontFamily:"monospace"}}>{n.date}</span></div><p style={{fontSize:12,color:"#cbd5e1",lineHeight:1.6,margin:0}}>{n.note}</p></div>)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MODULE 5 — ELIGIBILITY CHECK
// ════════════════════════════════════════════════════════════════════

function ModuleEligibility({token}){
  const [mid,setMid]=useState("");
  const [vehicle,setVehicle]=useState("");
  const [purpose,setPurpose]=useState("");
  const [miles,setMiles]=useState("");
  const [loading,setLoading]=useState(false);
  const [result,setResult]=useState(null);
  const [err,setErr]=useState("");
  const PURPOSES=["Dialysis","Primary Care","Specialist Appointment","Lab / Imaging","Hospital Discharge","Pharmacy","Physical Therapy","Mental Health"];
  const handle=async()=>{
    if(!mid.trim()){setErr("Member ID required.");return;}
    setErr("");setResult(null);setLoading(true);
    try{
      const params=new URLSearchParams({member_number:mid.trim().toUpperCase()});
      const res=await fetch(`${API_BASE}/eligibility/check?${params}`,{headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`}});
      const data=await res.json();
      if(!res.ok)throw new Error(data.error||"Check failed");
      if(!data.member&&!data.eligible){setErr("No member found with that ID.");setLoading(false);return;}
      setResult(data);
    }catch(e){setErr(e.message);}
    setLoading(false);
  };
  const iS={width:"100%",background:"#0f172a",border:"1px solid #1e293b",borderRadius:8,padding:"8px 12px",fontSize:13,color:"#e2e8f0",outline:"none",boxSizing:"border-box"};
  const lS={fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.09em",display:"block",marginBottom:5};
  const mem=result?.member;
  const ridesRem=result?.rides_remaining??0;
  const fmtPayer=v=>v?v.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase()):"";
  return(
    <div style={{background:"#080e1a",minHeight:"100%",color:"#e2e8f0",padding:"28px 24px"}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes fsi{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div style={{maxWidth:700,margin:"0 auto"}}>
        <div style={{marginBottom:24}}><h1 style={{fontSize:20,fontWeight:800,color:"#f1f5f9",margin:0}}>Eligibility Check</h1><p style={{fontSize:12,color:"#475569",marginTop:5}}>Verify member benefit eligibility before scheduling.</p></div>
        <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:14,padding:"20px",marginBottom:18}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
            <div style={{gridColumn:"1/-1"}}><label style={lS}>Member ID *</label><input style={{...iS,fontFamily:"monospace",fontSize:13}} value={mid} onChange={e=>setMid(e.target.value)} placeholder="e.g. HM-8820041" onKeyDown={e=>e.key==="Enter"&&handle()}/></div>
            <div><label style={lS}>Vehicle Type</label><select style={iS} value={vehicle} onChange={e=>setVehicle(e.target.value)}><option value="">— Any —</option>{VEHICLE_TYPES_LIST.map(v=><option key={v.id} value={v.id}>{v.icon} {v.label}</option>)}</select></div>
            <div><label style={lS}>Trip Purpose</label><select style={iS} value={purpose} onChange={e=>setPurpose(e.target.value)}><option value="">— Any —</option>{PURPOSES.map(p=><option key={p}>{p}</option>)}</select></div>
            <div><label style={lS}>Est. Miles</label><input style={iS} type="number" value={miles} onChange={e=>setMiles(e.target.value)} placeholder="e.g. 12" min={0}/></div>
          </div>
          {err&&<p style={{fontSize:12,color:"#f87171",background:"#1c0a0a",borderRadius:7,padding:"7px 11px",marginBottom:12}}>⚠ {err}</p>}
          <div style={{display:"flex",gap:8}}>
            <button onClick={handle} disabled={loading} style={{flex:1,background:loading?"#1e293b":"#3b82f6",border:"none",borderRadius:9,padding:"10px",fontSize:13,fontWeight:700,color:loading?"#475569":"#fff",cursor:loading?"default":"pointer"}}>
              {loading?<span style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8}}><span style={{width:13,height:13,border:"2px solid #475569",borderTopColor:"#94a3b8",borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>Checking…</span>:"Run Eligibility Check"}
            </button>
            {result&&<button onClick={()=>{setResult(null);setMid("");setVehicle("");setPurpose("");setMiles("");}} style={{background:"#1e293b",border:"none",borderRadius:9,padding:"10px 16px",fontSize:12,fontWeight:600,color:"#94a3b8",cursor:"pointer"}}>Reset</button>}
          </div>
        </div>
        {result&&mem&&(
          <div style={{animation:"fsi 0.4s ease both"}}>
            <div style={{borderRadius:12,padding:"14px 18px",marginBottom:14,display:"flex",alignItems:"center",gap:12,background:result.eligible?"#052e16":"#1c0a0a",border:`1px solid ${result.eligible?"#16a34a":"#991b1b"}`}}>
              <span style={{fontSize:28}}>{result.eligible?"✅":"❌"}</span>
              <div>
                <p style={{fontSize:15,fontWeight:800,color:result.eligible?"#4ade80":"#f87171",margin:0}}>{result.eligible?"Eligible — Ride may be scheduled":"Not Eligible — Review required"}</p>
                <p style={{fontSize:11,color:"#475569",margin:0,marginTop:2}}>{mem.first_name} {mem.last_name} · {fmtPayer(mem.payer)} · {mem.plan_id||""}</p>
                {!result.eligible&&result.reason&&<p style={{fontSize:12,color:"#f87171",margin:0,marginTop:4}}>Reason: {result.reason}</p>}
              </div>
              {result.eligible&&<button style={{marginLeft:"auto",background:"#16a34a",border:"none",borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,color:"#fff",cursor:"pointer"}}>Schedule →</button>}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:12}}>
              {[
                {l:"Rides Remaining",v:ridesRem,c:ridesRem>5?"#4ade80":ridesRem>0?"#fbbf24":"#f87171"},
                {l:"Eligible Through",v:mem.eligible_through?String(mem.eligible_through).slice(0,10):"—",c:"#38bdf8"},
                {l:"Rides Used / Allowed",v:`${mem.rides_used||0} / ${mem.rides_allowed||0}`,c:"#94a3b8"},
              ].map(s=>(
                <div key={s.l} style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:10,padding:"12px 14px"}}><p style={{fontSize:10,color:"#334155",textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:700,margin:0,marginBottom:4}}>{s.l}</p><p style={{fontSize:18,fontWeight:800,color:s.c,margin:0}}>{s.v}</p></div>
              ))}
            </div>
            {vehicle&&<div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:10,padding:"12px 14px"}}>
              <p style={{fontSize:10,color:"#334155",textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:700,margin:0,marginBottom:6}}>Request Parameters</p>
              <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                {vehicle&&<span style={{fontSize:12,color:"#94a3b8"}}>Vehicle: <b style={{color:"#e2e8f0"}}>{vehicle}</b></span>}
                {purpose&&<span style={{fontSize:12,color:"#94a3b8"}}>Purpose: <b style={{color:"#e2e8f0"}}>{purpose}</b></span>}
                {miles&&<span style={{fontSize:12,color:"#94a3b8"}}>Distance: <b style={{color:"#e2e8f0"}}>{miles} mi</b></span>}
              </div>
            </div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MODULE 6 — NOTIFICATIONS
// ════════════════════════════════════════════════════════════════════
function normalizeNotif(r){
  return{
    id:r.id,
    shortId:String(r.id).slice(0,8).toUpperCase(),
    rideId:r.ride_id||"",
    member:r.member_name||"Unknown",
    recipient:r.recipient||"",
    channel:r.channel||"sms",
    type:r.type||"confirmation",
    status:r.status||"pending",
    sentAt:r.sent_at?new Date(r.sent_at).toLocaleString("en-US"):"",
    body:r.body||"",
  };
}
const NOTIF_TPLS={
  confirmation:{label:"Booking Confirmation",icon:"✅",color:"#4ade80",sms:"Confirmed: Your NEMT ride is scheduled for {date} at {time}. Pickup: {pickup}. Driver will arrive in a {vehicle}. Confirmation #{rideId}. Questions? Call (956) 800-0000.",email:"Hi {memberName},\n\nYour ride has been confirmed.\n\nRide ID: {rideId}\nDate: {date} at {time}\nPickup: {pickup}\nDestination: {dropoff}\nVehicle: {vehicle}\n\nQuestions? Call (956) 800-0000.\n\nNEMT Hub"},
  reminder:{label:"Day-of Reminder",icon:"🔔",color:"#38bdf8",sms:"Reminder: Your ride is today at {time}. Driver will arrive in a {vehicle}. Confirmation #{rideId}. Reply STOP to opt out.",email:"Hi {memberName},\n\nReminder: Your ride is today at {time}.\nPickup: {pickup}\nDestination: {dropoff}\n\nQuestions? Call (956) 800-0000.\n\nNEMT Hub"},
  dispatch:{label:"Driver Dispatched",icon:"🚗",color:"#c4b5fd",sms:"Your driver {driverName} is on the way! ETA: {eta}. Vehicle: {vehicleDesc} ({plate}). Call (956) 800-0000. #{rideId}.",email:"Hi {memberName},\n\nYour driver is on the way!\n\nDriver: {driverName}\nETA: {eta}\nVehicle: {vehicleDesc} ({plate})\n\nNEMT Hub"},
  cancellation:{label:"Cancellation Notice",icon:"❌",color:"#f87171",sms:"Your ride {rideId} on {date} has been cancelled. To reschedule call (956) 800-0000.",email:"Hi {memberName},\n\nYour ride ({rideId}) on {date} at {time} has been cancelled.\n\nTo reschedule: (956) 800-0000.\n\nNEMT Hub"},
  completion:{label:"Trip Completed",icon:"🏁",color:"#fbbf24",sms:"Your ride is complete. Thank you for using NEMT services. To schedule a return trip, call (956) 800-0000.",email:"Hi {memberName},\n\nYour ride ({rideId}) has been completed. Thank you!\n\nNEMT Hub"},
};
function interpolate(t,v){return t.replace(/\{(\w+)\}/g,(_,k)=>v[k]||`{${k}}`);}
const CH_META={sms:{label:"SMS",icon:"💬",color:"#38bdf8"},email:{label:"Email",icon:"✉️",color:"#818cf8"}};
const NS_META={delivered:{label:"Delivered",color:"#4ade80",bg:"#052e16"},failed:{label:"Failed",color:"#f87171",bg:"#1c0a0a"},pending:{label:"Pending",color:"#fbbf24",bg:"#1c1100"}};
const TC={confirmation:"#4ade80",reminder:"#38bdf8",dispatch:"#c4b5fd",cancellation:"#f87171",completion:"#fbbf24"};

function ModuleNotifications({token}){
  const [tab,setTab]=useState("compose");
  const [log,setLog]=useState([]);
  const [logLoad,setLogLoad]=useState(false);
  const [logLoaded,setLogLoaded]=useState(false);
  const [tplKey,setTplKey]=useState("confirmation");
  const [channel,setChannel]=useState("sms");
  const [recipient,setRecipient]=useState("");
  const [sending,setSending]=useState(false);
  const [sent,setSent]=useState(false);
  const [fSt,setFSt]=useState("all");
  const [fCh,setFCh]=useState("all");
  const [expanded,setExpanded]=useState(null);
  const [vars]=useState({memberName:"Dorothy Nguyen",rideId:"RD-48821",date:"04/01/2026",time:"9:00 AM",pickup:"1204 Pecan Blvd",dropoff:"Rio Grande Dialysis Center",vehicle:"Wheelchair Van",driverName:"Tomás Garza",eta:"12 min",vehicleDesc:"Toyota Sienna",plate:"TXH-9034"});
  useEffect(()=>{
    if(tab!=="log"||logLoaded)return;
    setLogLoad(true);
    fetch(`${API_BASE}/notifications?limit=100`,{headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`}})
      .then(r=>r.json())
      .then(data=>{setLog((data.data||[]).map(normalizeNotif));setLogLoaded(true);setLogLoad(false);})
      .catch(()=>setLogLoad(false));
  },[tab,logLoaded]); // eslint-disable-line react-hooks/exhaustive-deps
  const tpl=NOTIF_TPLS[tplKey];
  const body=interpolate(channel==="sms"?tpl.sms:tpl.email,vars);
  const handleSend=async()=>{setSending(true);await sleep(1100);setSending(false);setSent(true);const newId=`MOCK-${Date.now()}`;setLog(p=>[{id:newId,shortId:newId.slice(0,8).toUpperCase(),rideId:"",member:"Manual Send",recipient,channel,type:tplKey,status:"delivered",sentAt:new Date().toLocaleString("en-US"),body},...p]);setTimeout(()=>setSent(false),2500);};
  const filtered=log.filter(n=>(fSt==="all"||n.status===fSt)&&(fCh==="all"||n.channel===fCh));
  const stats={total:log.length,delivered:log.filter(n=>n.status==="delivered").length,failed:log.filter(n=>n.status==="failed").length};
  const iS={width:"100%",background:"#0a1628",border:"1px solid #1e293b",borderRadius:7,padding:"7px 11px",fontSize:12,color:"#e2e8f0",outline:"none",boxSizing:"border-box"};
  return(
    <div style={{background:"#080e1a",minHeight:"100%",color:"#e2e8f0",padding:"24px"}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div><h1 style={{fontSize:20,fontWeight:800,color:"#f1f5f9",margin:0}}>Notifications</h1><p style={{fontSize:12,color:"#475569",marginTop:4}}>Send and manage SMS & email communications.</p></div>
        {stats.failed>0&&<div style={{display:"flex",alignItems:"center",gap:6,background:"#1c0a0a",border:"1px solid #991b1b",borderRadius:7,padding:"4px 10px"}}><span style={{width:6,height:6,borderRadius:"50%",background:"#f87171",display:"inline-block"}}/><span style={{fontSize:11,color:"#f87171",fontWeight:600}}>{stats.failed} failed</span></div>}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20}}>
        {[{l:"Total Sent",v:stats.total,c:"#e2e8f0"},{l:"Delivered",v:stats.delivered,c:"#4ade80"},{l:"Failed",v:stats.failed,c:"#f87171"}].map(s=>(
          <div key={s.l} style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:11,padding:"13px 15px"}}><p style={{fontSize:20,fontWeight:800,color:s.c,margin:0}}>{s.v}</p><p style={{fontSize:10,color:"#334155",textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:700,margin:0,marginTop:4}}>{s.l}</p></div>
        ))}
      </div>
      <div style={{display:"flex",gap:2,borderBottom:"1px solid #1e293b",marginBottom:18}}>
        {[["compose","✏️ Compose"],["log","📋 Log"]].map(([t,l])=><button key={t} onClick={()=>setTab(t)} style={{padding:"7px 18px",fontSize:12,fontWeight:600,border:"none",cursor:"pointer",background:"transparent",color:tab===t?"#38bdf8":"#475569",borderBottom:tab===t?"2px solid #38bdf8":"2px solid transparent"}}>{l}</button>)}
      </div>
      {tab==="compose"&&(
        <div style={{maxWidth:580,display:"flex",flexDirection:"column",gap:14}}>
          <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:12,padding:"16px"}}>
            <p style={{fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.09em",marginBottom:10}}>Type</p>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
              {Object.entries(NOTIF_TPLS).map(([k,t])=><button key={k} onClick={()=>setTplKey(k)} style={{background:tplKey===k?"#1e293b":"transparent",border:`1.5px solid ${tplKey===k?t.color:"#1e293b"}`,borderRadius:9,padding:"8px",cursor:"pointer",textAlign:"left"}}><p style={{fontSize:15,margin:0,marginBottom:2}}>{t.icon}</p><p style={{fontSize:10,fontWeight:700,color:tplKey===k?t.color:"#475569",margin:0}}>{t.label}</p></button>)}
            </div>
          </div>
          <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:12,padding:"16px"}}>
            <p style={{fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.09em",marginBottom:10}}>Channel & Recipient</p>
            <div style={{display:"flex",gap:8,marginBottom:10}}>
              {["sms","email"].map(c=><button key={c} onClick={()=>setChannel(c)} style={{flex:1,padding:"7px",borderRadius:8,border:`1.5px solid ${channel===c?CH_META[c].color:"#1e293b"}`,background:channel===c?"#1e293b":"transparent",fontSize:12,fontWeight:700,color:channel===c?CH_META[c].color:"#475569",cursor:"pointer"}}>{CH_META[c].icon} {CH_META[c].label}</button>)}
            </div>
            <input value={recipient} onChange={e=>setRecipient(e.target.value)} placeholder={channel==="sms"?"(956) 334-0912":"member@email.com"} style={iS}/>
          </div>
          <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:12,padding:"16px"}}>
            <p style={{fontSize:10,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.09em",marginBottom:10}}>Preview</p>
            <pre style={{fontSize:11,color:"#94a3b8",margin:0,whiteSpace:"pre-wrap",lineHeight:1.7,background:"#0a1628",borderRadius:7,padding:"10px 12px",fontFamily:"monospace"}}>{body}</pre>
            <p style={{fontSize:10,color:"#334155",marginTop:6}}>{body.length} chars</p>
          </div>
          <button onClick={handleSend} disabled={sending||sent} style={{background:sent?"#052e16":sending?"#1e293b":"#3b82f6",border:`1px solid ${sent?"#16a34a":"transparent"}`,borderRadius:10,padding:"11px",fontSize:13,fontWeight:700,color:sent?"#4ade80":sending?"#475569":"#fff",cursor:sending||sent?"default":"pointer"}}>
            {sent?"✓ Sent!":sending?<span style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8}}><span style={{width:13,height:13,border:"2px solid #475569",borderTopColor:"#94a3b8",borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>Sending…</span>:`Send ${CH_META[channel].label}`}
          </button>
        </div>
      )}
      {tab==="log"&&(
        <div>
          <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
            {[["all","All"],["delivered","Delivered"],["failed","Failed"]].map(([v,l])=><button key={v} onClick={()=>setFSt(v)} style={{padding:"4px 10px",borderRadius:6,fontSize:11,fontWeight:600,border:"none",cursor:"pointer",background:fSt===v?"#1e293b":"transparent",color:fSt===v?"#e2e8f0":"#475569"}}>{l}</button>)}
            <span style={{width:1,background:"#1e293b",margin:"0 4px"}}/>
            {[["all","All"],["sms","SMS"],["email","Email"]].map(([v,l])=><button key={v} onClick={()=>setFCh(v)} style={{padding:"4px 10px",borderRadius:6,fontSize:11,fontWeight:600,border:"none",cursor:"pointer",background:fCh===v?"#1e293b":"transparent",color:fCh===v?"#e2e8f0":"#475569"}}>{l}</button>)}
          </div>
          {logLoad&&<p style={{textAlign:"center",padding:"20px",color:"#475569",fontSize:13}}>Loading notifications…</p>}
          {!logLoad&&<div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:12,overflow:"hidden"}}>
            {filtered.length===0&&<p style={{textAlign:"center",padding:"20px",color:"#334155",fontSize:13}}>No notifications found.</p>}
            {filtered.map((n,i)=>{
              const sm=NS_META[n.status]||NS_META.pending;
              return(
                <div key={n.id} style={{borderBottom:"1px solid #1e293b",background:i%2===0?"#0a1628":"transparent"}}>
                  <div onClick={()=>setExpanded(expanded===n.id?null:n.id)} style={{display:"grid",gridTemplateColumns:"90px 100px 1fr 90px 70px 80px",alignItems:"center",padding:"9px 14px",cursor:"pointer",gap:8}}>
                    <span style={{fontSize:10,fontFamily:"monospace",color:"#38bdf8"}}>{n.shortId||String(n.id).slice(0,8).toUpperCase()}</span>
                    <span style={{fontSize:10,fontFamily:"monospace",color:"#475569",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{n.rideId||"—"}</span>
                    <span style={{fontSize:12,color:"#e2e8f0",fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{n.member}</span>
                    <span style={{fontSize:11,color:TC[n.type]||"#94a3b8",fontWeight:700,textTransform:"capitalize"}}>{n.type}</span>
                    <span style={{fontSize:11,color:CH_META[n.channel]?.color||"#94a3b8"}}>{CH_META[n.channel]?.icon} {CH_META[n.channel]?.label}</span>
                    <span style={{background:sm.bg,color:sm.color,borderRadius:99,padding:"2px 7px",fontSize:10,fontWeight:700}}>{sm.label}</span>
                  </div>
                  {expanded===n.id&&<div style={{padding:"0 14px 12px",borderTop:"1px solid #1e293b"}}><p style={{fontSize:10,color:"#475569",margin:"8px 0 6px"}}>Sent: {n.sentAt} · To: {n.recipient}</p><pre style={{fontSize:11,color:"#64748b",margin:0,background:"#0f172a",borderRadius:7,padding:"9px 11px",whiteSpace:"pre-wrap",fontFamily:"monospace",lineHeight:1.6}}>{n.body}</pre>{n.status==="failed"&&<button style={{marginTop:8,background:"#1e3a5f",border:"none",borderRadius:6,padding:"5px 12px",fontSize:11,fontWeight:700,color:"#60a5fa",cursor:"pointer"}}>↻ Retry</button>}</div>}
                </div>
              );
            })}
          </div>}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// UNIFIED SHELL
// ════════════════════════════════════════════════════════════════════
const NAV_ITEMS = [
  { id:"scheduler",  label:"Schedule Ride",   icon:"📋", role:"Call Center",   badge:null },
  { id:"provider",   label:"Dispatch Board",  icon:"🚗", role:"Provider",      badge:"2" },
  { id:"admin",      label:"Admin Dashboard", icon:"📊", role:"Admin",         badge:null },
  { id:"members",    label:"Members",         icon:"👥", role:"All",           badge:null },
  { id:"eligibility",label:"Eligibility",     icon:"✅", role:"All",           badge:null },
  { id:"notifications",label:"Notifications", icon:"🔔", role:"All",           badge:"1" },
];

const USER_ROLES = ["Call Center", "Provider", "Admin"];

export default function NEMTShell() {
  const [token, setToken]           = useState(() => sessionStorage.getItem("nemt_token") || null);
  const [currentUser, setCurrentUser] = useState(() => { try { return JSON.parse(sessionStorage.getItem("nemt_user")); } catch { return null; } });
  const [activeModule, setActiveModule] = useState("scheduler");
  const [userRole, setUserRole] = useState("Admin");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const handleLogin = (t, u) => {
    sessionStorage.setItem("nemt_token", t);
    sessionStorage.setItem("nemt_user", JSON.stringify(u));
    setToken(t);
    setCurrentUser(u);
  };

  const handleSignOut = () => {
    sessionStorage.removeItem("nemt_token");
    sessionStorage.removeItem("nemt_user");
    setToken(null);
    setCurrentUser(null);
  };

  if (!token) return <LoginScreen onLogin={handleLogin} />;

  const fmtTime = (d) => d.toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit" });

  const activeNav = NAV_ITEMS.find(n => n.id === activeModule);

  return (
    <div style={{ fontFamily:"'IBM Plex Sans','Segoe UI',sans-serif", display:"flex", height:"100vh", overflow:"hidden", background:"#080e1a", color:"#e2e8f0" }}>

      {/* ── Sidebar ── */}
      <div style={{ width: sidebarOpen ? 220 : 60, background:"#0a1628", borderRight:"1px solid #1e293b", display:"flex", flexDirection:"column", transition:"width 0.2s ease", flexShrink:0, overflow:"hidden" }}>

        {/* Logo */}
        <div style={{ padding:"16px 14px 12px", borderBottom:"1px solid #1e293b", display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
          <div style={{ width:32, height:32, borderRadius:9, background:"linear-gradient(135deg,#3b82f6,#06b6d4)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:900, color:"#fff", flexShrink:0 }}>N</div>
          {sidebarOpen && (
            <div style={{ minWidth:0 }}>
              <p style={{ fontSize:13, fontWeight:800, color:"#f1f5f9", margin:0, letterSpacing:"-0.02em", whiteSpace:"nowrap" }}>NEMT Hub</p>
              <p style={{ fontSize:10, color:"#334155", margin:0, marginTop:1, whiteSpace:"nowrap" }}>Operations Platform</p>
            </div>
          )}
        </div>

        {/* Nav Items */}
        <nav style={{ flex:1, padding:"10px 8px", overflowY:"auto" }}>
          {NAV_ITEMS.map(item => {
            const active = activeModule === item.id;
            return (
              <button key={item.id} onClick={() => setActiveModule(item.id)} style={{
                width:"100%", display:"flex", alignItems:"center", gap:10, padding: sidebarOpen ? "9px 10px" : "9px",
                borderRadius:9, border:"none", cursor:"pointer", marginBottom:2,
                background: active ? "#1e3a5f" : "transparent",
                borderLeft: active ? "2px solid #3b82f6" : "2px solid transparent",
                transition:"all 0.15s", justifyContent: sidebarOpen ? "flex-start" : "center",
                position:"relative"
              }}>
                <span style={{ fontSize:16, flexShrink:0 }}>{item.icon}</span>
                {sidebarOpen && (
                  <span style={{ fontSize:12, fontWeight:600, color: active ? "#60a5fa" : "#64748b", whiteSpace:"nowrap", flex:1, textAlign:"left" }}>{item.label}</span>
                )}
                {item.badge && sidebarOpen && (
                  <span style={{ background:"#ef4444", color:"#fff", borderRadius:99, padding:"1px 6px", fontSize:10, fontWeight:700 }}>{item.badge}</span>
                )}
                {item.badge && !sidebarOpen && (
                  <span style={{ position:"absolute", top:6, right:6, width:8, height:8, background:"#ef4444", borderRadius:"50%" }} />
                )}
              </button>
            );
          })}
        </nav>

        {/* Bottom: role selector + collapse */}
        <div style={{ borderTop:"1px solid #1e293b", padding:"10px 8px" }}>
          {sidebarOpen && (
            <div style={{ marginBottom:8 }}>
              <p style={{ fontSize:9, fontWeight:700, color:"#334155", textTransform:"uppercase", letterSpacing:"0.09em", margin:"0 0 5px 2px" }}>View As</p>
              <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                {USER_ROLES.map(r => (
                  <button key={r} onClick={() => setUserRole(r)} style={{ background: userRole===r?"#1e293b":"transparent", border:`1px solid ${userRole===r?"#3b82f6":"transparent"}`, borderRadius:7, padding:"5px 8px", fontSize:11, fontWeight:600, color: userRole===r?"#60a5fa":"#475569", cursor:"pointer", textAlign:"left" }}>{r}</button>
                ))}
              </div>
            </div>
          )}
          <button onClick={() => setSidebarOpen(s => !s)} style={{ width:"100%", background:"#1e293b", border:"none", borderRadius:7, padding:"7px", fontSize:12, color:"#475569", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
            <span style={{ fontSize:13 }}>{sidebarOpen ? "◀" : "▶"}</span>
            {sidebarOpen && <span style={{ fontSize:11, fontWeight:600 }}>Collapse</span>}
          </button>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>

        {/* Top Bar */}
        <div style={{ height:48, background:"#0a1628", borderBottom:"1px solid #1e293b", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 20px", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:16 }}>{activeNav?.icon}</span>
            <span style={{ fontSize:14, fontWeight:700, color:"#f1f5f9" }}>{activeNav?.label}</span>
            <span style={{ fontSize:11, color:"#334155", marginLeft:4 }}>· {activeNav?.role}</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ display:"flex", alignItems:"center", gap:5, background:"#0f172a", border:"1px solid #1e293b", borderRadius:7, padding:"4px 10px" }}>
              <span style={{ width:6, height:6, borderRadius:"50%", background:"#4ade80", display:"inline-block", boxShadow:"0 0 5px #4ade80" }} />
              <span style={{ fontSize:11, color:"#94a3b8", fontWeight:500 }}>Live · {fmtTime(time)}</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:26, height:26, borderRadius:"50%", background:"linear-gradient(135deg,#3b82f6,#06b6d4)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:800, color:"#fff" }}>
                {currentUser ? `${currentUser.first_name?.[0]||""}${currentUser.last_name?.[0]||""}` : "?"}
              </div>
              {sidebarOpen && <span style={{ fontSize:12, fontWeight:600, color:"#64748b" }}>{currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : ""}</span>}
              <button onClick={handleSignOut} style={{ fontSize:11, color:"#475569", background:"transparent", border:"1px solid #1e293b", borderRadius:6, padding:"3px 8px", cursor:"pointer" }}>Sign out</button>
            </div>
          </div>
        </div>

        {/* Module Content */}
        <div style={{ flex:1, overflowY:"auto" }}>
          {activeModule === "scheduler"    && <ModuleScheduler token={token} />}
          {activeModule === "provider"     && <ModuleProvider token={token} />}
          {activeModule === "admin"        && <ModuleAdmin />}
          {activeModule === "members"      && <ModuleMembers token={token} />}
          {activeModule === "eligibility"  && <ModuleEligibility token={token} />}
          {activeModule === "notifications"&& <ModuleNotifications token={token} />}
        </div>
      </div>
    </div>
  );
}
